import React, { useRef, useEffect, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { Eraser, Check } from 'lucide-react';

const SignaturePad = forwardRef(({
  value,
  onChange,
  disabled = false,
  height = 180,
}, ref) => {
  const sigPadRef = useRef(null);
  const containerRef = useRef(null);
  const lastDataUrlRef = useRef(value || null);

  // Resize the underlying canvas to match container width AND device pixel ratio.
  // This is the single biggest fix for signature accuracy: without DPR scaling
  // the strokes are coarse and offset on retina / high-DPI screens.
  const resizeCanvas = useCallback(() => {
    const sig = sigPadRef.current;
    const container = containerRef.current;
    if (!sig || !container) return;

    const canvas = sig.getCanvas();
    const ctx = canvas.getContext('2d');
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const cssWidth = container.clientWidth;
    const cssHeight = height;

    // Preserve current strokes
    const existing = !sig.isEmpty() ? sig.toDataURL('image/png') : (lastDataUrlRef.current || null);

    canvas.width = cssWidth * ratio;
    canvas.height = cssHeight * ratio;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset before scaling
    ctx.scale(ratio, ratio);

    sig.clear();
    if (existing) {
      try {
        sig.fromDataURL(existing, { ratio, width: cssWidth, height: cssHeight });
      } catch (e) {
        // fromDataURL can throw on malformed data URLs — log so it's not silent.
        console.warn('SignaturePad: failed to restore signature on resize', e);
      }
    }
  }, [height]);

  useEffect(() => {
    resizeCanvas();
    const onResize = () => resizeCanvas();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [resizeCanvas]);

  // Load external value when it changes (e.g. form load)
  useEffect(() => {
    const sig = sigPadRef.current;
    if (!sig) return;
    if (value && value !== lastDataUrlRef.current) {
      try {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const cssWidth = containerRef.current?.clientWidth || 400;
        sig.clear();
        sig.fromDataURL(value, { ratio, width: cssWidth, height });
        lastDataUrlRef.current = value;
      } catch (e) {
        // fromDataURL can throw on malformed data URLs — log so it's not silent.
        console.warn('SignaturePad: failed to load value', e);
      }
    }
  }, [value, height]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      if (sigPadRef.current) {
        sigPadRef.current.clear();
        lastDataUrlRef.current = null;
        onChange(null);
      }
    },
    getSignature: () => {
      if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
        return sigPadRef.current.toDataURL('image/png');
      }
      return null;
    },
    isEmpty: () => (sigPadRef.current ? sigPadRef.current.isEmpty() : true),
  }));

  const handleEnd = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const dataUrl = sigPadRef.current.toDataURL('image/png');
      lastDataUrlRef.current = dataUrl;
      onChange(dataUrl);
    }
  };

  const handleClear = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      lastDataUrlRef.current = null;
      onChange(null);
    }
  };

  // Memoize canvasProps so we don't pass a new object reference on every render,
  // which would otherwise force SignatureCanvas to re-render unnecessarily.
  const canvasProps = useMemo(() => ({
    className: 'signature-canvas block',
    'data-testid': 'signature-canvas',
    style: { display: 'block', touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' },
  }), [disabled]);

  return (
    <div className="space-y-2" ref={containerRef}>
      <div
        className={`relative border-2 border-slate-300 rounded-lg bg-white overflow-hidden ${disabled ? 'opacity-60' : ''}`}
        style={{ touchAction: 'none' }}
      >
        <SignatureCanvas
          ref={sigPadRef}
          penColor="#0f172a"
          // High-fidelity pen tuning for accurate, smooth strokes
          minWidth={0.6}
          maxWidth={2.4}
          velocityFilterWeight={0.6}
          throttle={8}
          minDistance={1}
          dotSize={1.4}
          onEnd={handleEnd}
          canvasProps={canvasProps}
        />
        {disabled && <div className="absolute inset-0 cursor-not-allowed" />}
      </div>

      {!disabled && (
        <div className="flex gap-2 items-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClear}
            data-testid="clear-signature-btn"
          >
            <Eraser className="h-4 w-4 mr-1" />
            Clear
          </Button>
          {value && (
            <span className="text-sm text-green-600 flex items-center" data-testid="signature-captured-indicator">
              <Check className="h-4 w-4 mr-1" />
              Signature captured
            </span>
          )}
        </div>
      )}
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
